import React, { useState, useEffect } from "react";
import {
  Zap,
  Split,
  Globe,
  Briefcase,
  User,
  Building,
  CheckSquare,
  Trash2,
  Plus,
  ArrowLeft,
  Save,
  Play,
  History,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type {
  Automation,
  AutomationStep,
  TriggerItem,
  TriggerEvent,
  ConditionConfig,
  ActionConfig,
} from "./types";
import {
  TRIGGER_ITEM_LABELS,
  TRIGGER_EVENT_LABELS,
  ACTION_TYPE_LABELS,
  ITEM_AVAILABLE_FIELDS,
  CONDITION_OPERATORS,
} from "./constants";
import { TriggerSheet } from "./TriggerSheet";
import { ConditionSheet } from "./ConditionSheet";
import { ActionSheet } from "./ActionSheet";
import { WebhookSheet } from "./WebhookSheet";
import {
  createAutomation,
  updateAutomation,
  triggerManualTest,
} from "./automationsApi";

interface AutomationVisualBuilderProps {
  initialAutomation?: Automation | null;
  onBack: () => void;
  onOpenHistory: (automationId?: number) => void;
  crmId?: string;
}

export const AutomationVisualBuilder: React.FC<
  AutomationVisualBuilderProps
> = ({ initialAutomation, onBack, onOpenHistory, crmId }) => {
  // State da automação
  const [id, setId] = useState<number | undefined>(initialAutomation?.id);
  const [nome, setNome] = useState<string>(
    initialAutomation?.nome || "Nova Automação",
  );
  const [ativa, setAtiva] = useState<boolean>(initialAutomation?.ativa ?? true);
  const [gatilhoItem, setGatilhoItem] = useState<TriggerItem>(
    initialAutomation?.gatilho_item || "negocio",
  );
  const [gatilhoEvento, setGatilhoEvento] = useState<TriggerEvent>(
    initialAutomation?.gatilho_evento || "criado",
  );
  const [primeiraAtividade, setPrimeiraAtividade] = useState<boolean>(
    initialAutomation?.primeira_atividade_negocio || false,
  );
  const [passos, setPassos] = useState<AutomationStep[]>(
    initialAutomation?.automacao_passos || [],
  );

  // Sheets state
  const [triggerSheetOpen, setTriggerSheetOpen] = useState(false);
  const [conditionSheetOpen, setConditionSheetOpen] = useState(false);
  const [actionSheetOpen, setActionSheetOpen] = useState(false);
  const [webhookSheetOpen, setWebhookSheetOpen] = useState(false);

  // Passo atualmente em edição (por step_key)
  const [activeStepKey, setActiveStepKey] = useState<string | null>(null);

  // Local para inserção de novo passo (passo anterior + ramo + próximo passo)
  const [insertTarget, setInsertTarget] = useState<{
    passo_anterior_step_key?: string | null;
    ramo_id?: string | null;
    proximo_step_key?: string | null;
  }>({});

  const [saving, setSaving] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  useEffect(() => {
    if (initialAutomation) {
      setId(initialAutomation.id);
      setNome(initialAutomation.nome);
      setAtiva(initialAutomation.ativa);
      setGatilhoItem(initialAutomation.gatilho_item);
      setGatilhoEvento(initialAutomation.gatilho_evento);
      setPrimeiraAtividade(initialAutomation.primeira_atividade_negocio);
      setPassos(initialAutomation.automacao_passos || []);
    }
  }, [initialAutomation]);

  // Limpa feedback após 3s
  useEffect(() => {
    if (feedbackMsg) {
      const t = setTimeout(() => setFeedbackMsg(null), 3500);
      return () => clearTimeout(t);
    }
  }, [feedbackMsg]);

  // Gera chave única para novo passo (ex: 'p1', 'p2', etc.)
  const generateStepKey = (): string => {
    const keys = passos.map((p) => {
      const num = parseInt(p.step_key.replace("p", ""), 10);
      return isNaN(num) ? 0 : num;
    });
    const max = keys.length > 0 ? Math.max(...keys) : 0;
    return `p${max + 1}`;
  };

  // Abrir criação de novo passo
  const handleStartAddStep = (
    tipo: "condicao" | "acao_crm" | "webhook",
    passoAnteriorStepKey?: string | null,
    ramoId?: string | null,
    proximoStepKey?: string | null,
  ) => {
    setInsertTarget({
      passo_anterior_step_key: passoAnteriorStepKey || null,
      ramo_id: ramoId || null,
      proximo_step_key: proximoStepKey || null,
    });

    if (tipo === "condicao") {
      setActiveStepKey(null);
      setConditionSheetOpen(true);
    } else if (tipo === "acao_crm") {
      setActiveStepKey(null);
      setActionSheetOpen(true);
    } else if (tipo === "webhook") {
      setActiveStepKey(null);
      setWebhookSheetOpen(true);
    }
  };

  // Helper para inserir novo passo conectando corretamente na cadeia
  const insertNewStep = (tipo: "condicao" | "acao", config: any) => {
    const newKey = generateStepKey();
    const newStep: AutomationStep = {
      step_key: newKey,
      tipo,
      passo_anterior_step_key: insertTarget.passo_anterior_step_key || null,
      ramo_id: insertTarget.ramo_id || null,
      ordem: passos.length,
      config,
    };

    setPassos((prev) => {
      let updated = [...prev];
      if (insertTarget.proximo_step_key) {
        updated = updated.map((p) => {
          if (p.step_key === insertTarget.proximo_step_key) {
            return {
              ...p,
              passo_anterior_step_key: newKey,
            };
          }
          return p;
        });
      }
      return [...updated, newStep];
    });

    setInsertTarget({});
  };

  // Salvar Condição (novo ou editado)
  const handleSaveCondition = (config: ConditionConfig) => {
    if (activeStepKey) {
      setPassos((prev) =>
        prev.map((p) => (p.step_key === activeStepKey ? { ...p, config } : p)),
      );
      setActiveStepKey(null);
    } else {
      insertNewStep("condicao", config);
    }
  };

  // Salvar Ação CRM (novo ou editado)
  const handleSaveAction = (config: ActionConfig) => {
    if (activeStepKey) {
      setPassos((prev) =>
        prev.map((p) => (p.step_key === activeStepKey ? { ...p, config } : p)),
      );
      setActiveStepKey(null);
    } else {
      insertNewStep("acao", config);
    }
  };

  // Salvar Webhook (novo ou editado)
  const handleSaveWebhook = (config: ActionConfig) => {
    if (activeStepKey) {
      setPassos((prev) =>
        prev.map((p) => (p.step_key === activeStepKey ? { ...p, config } : p)),
      );
      setActiveStepKey(null);
    } else {
      insertNewStep("acao", config);
    }
  };

  // Remover passo e reconectar filhos lineares
  const handleDeleteStep = (stepKey: string) => {
    const stepToDelete = passos.find((p) => p.step_key === stepKey);
    if (!stepToDelete) return;

    if (stepToDelete.tipo === "condicao") {
      // Condição pode ter bifurcações em múltiplos ramos. Deleta a árvore abaixo dela.
      const toDelete = new Set<string>([stepKey]);
      let added = true;
      while (added) {
        added = false;
        for (const p of passos) {
          if (
            p.passo_anterior_step_key &&
            toDelete.has(p.passo_anterior_step_key) &&
            !toDelete.has(p.step_key)
          ) {
            toDelete.add(p.step_key);
            added = true;
          }
        }
      }
      setPassos((prev) => prev.filter((p) => !toDelete.has(p.step_key)));
    } else {
      // Passo linear: reconecta os nós filhos ao pai do nó excluído para não perder os passos subsequentes
      const parentKey = stepToDelete.passo_anterior_step_key || null;
      const parentRamo = stepToDelete.ramo_id || null;

      setPassos((prev) =>
        prev
          .filter((p) => p.step_key !== stepKey)
          .map((p) => {
            if (p.passo_anterior_step_key === stepKey) {
              return {
                ...p,
                passo_anterior_step_key: parentKey,
                ramo_id: p.ramo_id || parentRamo,
              };
            }
            return p;
          }),
      );
    }
  };

  // Abrir edição de passo existente
  const handleEditStep = (stepKey: string) => {
    const step = passos.find((p) => p.step_key === stepKey);
    if (!step) return;
    setActiveStepKey(stepKey);

    if (step.tipo === "condicao") {
      setConditionSheetOpen(true);
    } else if (step.tipo === "acao") {
      if (step.config?.tipo_acao === "webhook") {
        setWebhookSheetOpen(true);
      } else {
        setActionSheetOpen(true);
      }
    }
  };

  // Salvar automação completa no servidor
  const handleSaveFullAutomation = async () => {
    if (!nome.trim()) {
      setFeedbackMsg({
        type: "error",
        text: "Digite um nome para a automação.",
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nome,
        ativa,
        gatilho_item: gatilhoItem,
        gatilho_evento: gatilhoEvento,
        primeira_atividade_negocio: primeiraAtividade,
        passos,
      };

      if (id) {
        const res = await updateAutomation(id, payload, crmId);
        setId(res.data.id);
        setFeedbackMsg({
          type: "success",
          text: "Automação atualizada com sucesso!",
        });
      } else {
        const res = await createAutomation(payload, crmId);
        setId(res.data.id);
        setFeedbackMsg({
          type: "success",
          text: "Automação criada com sucesso!",
        });
      }
    } catch (err: any) {
      setFeedbackMsg({
        type: "error",
        text: err.message || "Erro ao salvar automação.",
      });
    } finally {
      setSaving(false);
    }
  };

  // Disparo manual para testes rápidos
  const handleQuickTest = async () => {
    try {
      await triggerManualTest(
        {
          item: gatilhoItem,
          evento: gatilhoEvento,
          registro_id: "teste_manual",
          dados: {
            id: 999999,
            name: "Lead Teste Manual",
            stage: "qualificado",
            status: "open",
            amount: 15000,
            first_name: "Cliente",
            last_name: "Teste",
            text: "Atividade de teste",
          },
        },
        crmId,
      );
      setFeedbackMsg({
        type: "success",
        text: "Evento de teste disparado! Veja o resultado no Histórico de Execuções.",
      });
    } catch (err: any) {
      setFeedbackMsg({ type: "error", text: err.message || "Falha no teste." });
    }
  };

  // Helpers de renderização dos passos
  const rootSteps = passos.filter((p) => !p.passo_anterior_step_key);

  return (
    <div className="flex flex-col h-full bg-background min-h-screen">
      {/* ── TOPBAR DO CONSTRUTOR ──────────────────────────────────────────────── */}
      <header className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-3 px-6 py-3.5 border-b border-border/80 bg-card/80 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="h-8 gap-1 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Automações</span>
          </Button>

          <div className="h-4 w-[1px] bg-border" />

          {/* Nome da Automação Editável */}
          <div className="flex items-center gap-2">
            <Input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Nome da Automação..."
              className="h-8 text-sm font-bold bg-transparent border-transparent hover:border-border focus:border-primary focus:bg-background transition-all w-60 sm:w-80"
            />
          </div>
        </div>

        {/* Ações da Topbar */}
        <div className="flex items-center gap-3">
          {/* Status Ativa / Inativa */}
          <div className="flex items-center gap-2 px-3 py-1 rounded-full border border-border/80 bg-muted/40">
            <span
              className={`text-xs font-semibold ${
                ativa ? "text-emerald-500" : "text-muted-foreground"
              }`}
            >
              {ativa ? "Ativa" : "Pausada"}
            </span>
            <Switch checked={ativa} onCheckedChange={setAtiva} />
          </div>

          {/* Histórico */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenHistory(id)}
            className="h-8 text-xs gap-1.5 border-border/80 hover:bg-muted"
          >
            <History className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="hidden sm:inline">Histórico</span>
          </Button>

          {/* Disparo de Teste */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleQuickTest}
            className="h-8 text-xs gap-1.5 border-border/80 hover:bg-muted"
            title="Dispara um evento de teste simulado para testar as ações"
          >
            <Play className="w-3.5 h-3.5 text-amber-500" />
            <span className="hidden sm:inline">Testar</span>
          </Button>

          {/* Salvar */}
          <Button
            size="sm"
            disabled={saving}
            onClick={handleSaveFullAutomation}
            className="h-8 text-xs gap-1.5 font-semibold shadow-sm"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </header>

      {/* FEEDBACK ALERT */}
      {feedbackMsg && (
        <div
          className={`px-6 py-2 text-xs font-medium text-center border-b transition-all ${
            feedbackMsg.type === "success"
              ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
              : "bg-rose-500/10 text-rose-600 border-rose-500/20"
          }`}
        >
          {feedbackMsg.text}
        </div>
      )}

      {/* ── CANVAS VISUAL DO FLUXOGRAMA (ESTILO PIPEDRIVE) ──────────────────── */}
      <main className="flex-1 overflow-auto p-6 flex flex-col items-center">
        <div className="w-full max-w-2xl flex flex-col items-center">
          {/* 1. NÓ DO GATILHO (O Início) */}
          <div className="w-full relative group">
            <div
              onClick={() => setTriggerSheetOpen(true)}
              className="p-5 rounded-2xl border-2 border-primary/40 hover:border-primary bg-card/90 hover:bg-card shadow-sm hover:shadow-md transition-all cursor-pointer relative"
            >
              <div className="flex items-center justify-between mb-3">
                <Badge
                  variant="outline"
                  className="gap-1 bg-primary/10 text-primary border-primary/30"
                >
                  <Zap className="w-3.5 h-3.5" />
                  Gatilho de Entrada
                </Badge>
                <span className="text-xs text-muted-foreground group-hover:text-primary transition-colors font-medium">
                  Configurar ⚙️
                </span>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10 text-primary shrink-0">
                  {gatilhoItem === "negocio" && (
                    <Briefcase className="w-5 h-5" />
                  )}
                  {gatilhoItem === "pessoa" && <User className="w-5 h-5" />}
                  {gatilhoItem === "organizacao" && (
                    <Building className="w-5 h-5" />
                  )}
                  {gatilhoItem === "atividade" && (
                    <CheckSquare className="w-5 h-5" />
                  )}
                </div>

                <div className="min-w-0">
                  <h3 className="font-bold text-sm text-foreground">
                    Quando {TRIGGER_ITEM_LABELS[gatilhoItem].label} for{" "}
                    {TRIGGER_EVENT_LABELS[gatilhoEvento].label.toLowerCase()}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                    {TRIGGER_ITEM_LABELS[gatilhoItem].desc}
                    {gatilhoItem === "atividade" && primeiraAtividade
                      ? " • Apenas na 1ª atividade"
                      : ""}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CONECTOR VERTICAL COM BOTÃO + (Insere antes do primeiro passo ou adiciona o primeiro) */}
          <StepConnector
            onAdd={(tipo) =>
              handleStartAddStep(
                tipo,
                null,
                null,
                rootSteps[0]?.step_key || null,
              )
            }
          />

          {/* 2. LISTA DE PASSOS (GRAFO) */}
          {rootSteps.length === 0 ? (
            <div className="p-6 text-center border border-dashed border-border/80 rounded-2xl w-full text-muted-foreground bg-muted/10">
              <p className="text-xs">Nenhum passo adicionado ainda.</p>
              <p className="text-[11px] text-muted-foreground/80 mt-1">
                Clique no botão <strong>+</strong> acima para adicionar uma
                Condição ou Ação.
              </p>
            </div>
          ) : (
            <div className="w-full flex flex-col items-center">
              {rootSteps.map((step) => (
                <RenderStepNode
                  key={step.step_key}
                  step={step}
                  allSteps={passos}
                  onEdit={handleEditStep}
                  onDelete={handleDeleteStep}
                  onAddChild={(tipo, parentKey, ramoId, proximoKey) =>
                    handleStartAddStep(tipo, parentKey, ramoId, proximoKey)
                  }
                />
              ))}
            </div>
          )}

          {/* 3. TÉRMINO DO FLUXO */}
          <div className="mt-8 flex flex-col items-center">
            <div className="px-4 py-1.5 rounded-full border border-border/80 bg-muted/60 text-[11px] font-semibold text-muted-foreground shadow-xs">
              🏁 Fim da Execução
            </div>
          </div>
        </div>
      </main>

      {/* ── SHEETS LATERAIS ───────────────────────────────────────────────────── */}
      {(() => {
        const activeStep = passos.find((p) => p.step_key === activeStepKey);
        return (
          <>
            <TriggerSheet
              open={triggerSheetOpen}
              onOpenChange={setTriggerSheetOpen}
              item={gatilhoItem}
              evento={gatilhoEvento}
              primeiraAtividade={primeiraAtividade}
              onSave={(data) => {
                setGatilhoItem(data.gatilho_item);
                setGatilhoEvento(data.gatilho_evento);
                setPrimeiraAtividade(data.primeira_atividade_negocio);
              }}
            />

            <ConditionSheet
              open={conditionSheetOpen}
              onOpenChange={(o) => {
                setConditionSheetOpen(o);
                if (!o) setActiveStepKey(null);
              }}
              triggerItem={gatilhoItem}
              config={
                activeStep && activeStep.tipo === "condicao"
                  ? (activeStep.config as ConditionConfig)
                  : { ramos: [] }
              }
              onSave={handleSaveCondition}
            />

            <ActionSheet
              open={actionSheetOpen}
              onOpenChange={(o) => {
                setActionSheetOpen(o);
                if (!o) setActiveStepKey(null);
              }}
              triggerItem={gatilhoItem}
              config={
                activeStep && activeStep.tipo === "acao"
                  ? (activeStep.config as ActionConfig)
                  : { tipo_acao: "criar_atividade" }
              }
              onSave={handleSaveAction}
            />

            <WebhookSheet
              open={webhookSheetOpen}
              onOpenChange={(o) => {
                setWebhookSheetOpen(o);
                if (!o) setActiveStepKey(null);
              }}
              triggerItem={gatilhoItem}
              config={
                activeStep && activeStep.tipo === "acao"
                  ? (activeStep.config as ActionConfig)
                  : { tipo_acao: "webhook" }
              }
              onSave={handleSaveWebhook}
            />
          </>
        );
      })()}
    </div>
  );
};

// ── COMPONENTE: CONECTOR VERTICAL COM BOTÃO + ─────────────────────────────────
const StepConnector: React.FC<{
  onAdd: (tipo: "condicao" | "acao_crm" | "webhook") => void;
}> = ({ onAdd }) => {
  return (
    <div className="flex flex-col items-center my-1 relative group">
      <div className="w-0.5 h-6 bg-border/80 group-hover:bg-primary/60 transition-colors" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 w-7 p-0 rounded-full border-border/80 hover:border-primary hover:bg-primary hover:text-primary-foreground shadow-xs transition-all cursor-pointer z-10"
            title="Adicionar próximo passo"
          >
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-56 text-xs">
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Adicionar Passo
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => onAdd("condicao")}
            className="gap-2 cursor-pointer"
          >
            <Split className="w-3.5 h-3.5 text-emerald-500" />
            <span>Condição (Se / Senão)</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onAdd("acao_crm")}
            className="gap-2 cursor-pointer"
          >
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span>Ação no CRM</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onAdd("webhook")}
            className="gap-2 cursor-pointer"
          >
            <Globe className="w-3.5 h-3.5 text-sky-500" />
            <span>Webhook HTTP POST</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="w-0.5 h-6 bg-border/80 group-hover:bg-primary/60 transition-colors" />
    </div>
  );
};

// ── COMPONENTE: RENDERIZADOR DE NÓS (CONDIÇÃO OU AÇÃO) ────────────────────────
const RenderStepNode: React.FC<{
  step: AutomationStep;
  allSteps: AutomationStep[];
  onEdit: (stepKey: string) => void;
  onDelete: (stepKey: string) => void;
  onAddChild: (
    tipo: "condicao" | "acao_crm" | "webhook",
    parentStepKey?: string | null,
    ramoId?: string | null,
    proximoStepKey?: string | null,
  ) => void;
}> = ({ step, allSteps, onEdit, onDelete, onAddChild }) => {
  // Se for CONDICIONAL
  if (step.tipo === "condicao") {
    const config = step.config as ConditionConfig;
    const ramos = config?.ramos || [];

    // Filhos deste nó para cada ramo
    const getChildrenForBranch = (ramoId: string) => {
      return allSteps.filter(
        (p) =>
          p.passo_anterior_step_key === step.step_key && p.ramo_id === ramoId,
      );
    };

    const fallbackChildren = allSteps.filter(
      (p) =>
        p.passo_anterior_step_key === step.step_key &&
        (p.ramo_id === "nenhuma_atendida" || !p.ramo_id),
    );

    return (
      <div className="w-full flex flex-col items-center">
        {/* CARD PRINCIPAL DA CONDIÇÃO */}
        <div className="w-full p-4 rounded-xl border border-emerald-500/40 bg-card shadow-xs hover:border-emerald-500 transition-all">
          <div className="flex items-center justify-between mb-2">
            <Badge
              variant="outline"
              className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 gap-1 text-[11px]"
            >
              <Split className="w-3 h-3" />
              Condição & Ramificação
            </Badge>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(step.step_key)}
                className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground cursor-pointer"
              >
                Editar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(step.step_key)}
                className="h-6 w-6 p-0 text-muted-foreground hover:text-rose-600 cursor-pointer"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>

          <div className="space-y-1.5 text-xs">
            {ramos.map((r, i) => {
              const allCondFields = Object.values(ITEM_AVAILABLE_FIELDS).flat();
              const formattedRules =
                r.regras
                  ?.map((rg) => {
                    const fDef = allCondFields.find(
                      (f) => f.field === rg.campo,
                    );
                    const fLabel = fDef?.label || rg.campo;
                    const opLabel =
                      CONDITION_OPERATORS.find((o) => o.value === rg.operador)
                        ?.label || rg.operador;
                    const valLabel =
                      fDef?.options?.find((o) => o.value === String(rg.valor))
                        ?.label ??
                      rg.valor ??
                      "";
                    return `${fLabel} ${opLabel.toLowerCase()} ${valLabel ? `"${valLabel}"` : ""}`.trim();
                  })
                  .join(` ${r.operador_logico?.toUpperCase() || "E"} `) ||
                "Sem regras";

              return (
                <div
                  key={r.id || i}
                  className="flex items-center gap-1.5 text-muted-foreground"
                >
                  <span className="font-semibold text-foreground">
                    {r.nome || `Ramo ${i + 1}`}:
                  </span>
                  <span>{formattedRules}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* RAMIFICAÇÕES LADO A LADO */}
        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t-2 border-dashed border-border/80">
          {/* 1. RAMOS CONDICIONAIS */}
          {ramos.map((ramo) => {
            const branchChildren = getChildrenForBranch(ramo.id);
            return (
              <div
                key={ramo.id}
                className="flex flex-col items-center p-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 min-w-[240px]"
              >
                <div className="px-2.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-bold text-[10px] uppercase mb-2">
                  ✓ {ramo.nome || "Se atendido"}
                </div>

                {branchChildren.map((cStep) => (
                  <RenderStepNode
                    key={cStep.step_key}
                    step={cStep}
                    allSteps={allSteps}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onAddChild={onAddChild}
                  />
                ))}

                {branchChildren.length === 0 && (
                  <StepConnector
                    onAdd={(tipo) =>
                      onAddChild(tipo, step.step_key, ramo.id, null)
                    }
                  />
                )}
              </div>
            );
          })}

          {/* 2. CAMINHO PADRÃO (SENÃO) */}
          <div className="flex flex-col items-center p-3 rounded-xl border border-border/60 bg-muted/20 min-w-[240px]">
            <div className="px-2.5 py-0.5 rounded-full bg-muted text-muted-foreground font-bold text-[10px] uppercase mb-2">
              ✗ Senão (Padrão)
            </div>

            {fallbackChildren.map((cStep) => (
              <RenderStepNode
                key={cStep.step_key}
                step={cStep}
                allSteps={allSteps}
                onEdit={onEdit}
                onDelete={onDelete}
                onAddChild={onAddChild}
              />
            ))}

            {fallbackChildren.length === 0 && (
              <StepConnector
                onAdd={(tipo) =>
                  onAddChild(tipo, step.step_key, "nenhuma_atendida", null)
                }
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Se for AÇÃO DO CRM OU WEBHOOK
  const isWebhook = step.config?.tipo_acao === "webhook";
  const actionMeta =
    ACTION_TYPE_LABELS[
      step.config?.tipo_acao as keyof typeof ACTION_TYPE_LABELS
    ];

  // Filhos deste nó linear
  const childSteps = allSteps.filter(
    (p) => p.passo_anterior_step_key === step.step_key,
  );

  return (
    <div className="w-full flex flex-col items-center">
      <div
        className={`w-full p-4 rounded-xl border bg-card shadow-xs transition-all ${
          isWebhook
            ? "border-sky-500/40 hover:border-sky-500"
            : "border-primary/40 hover:border-primary"
        }`}
      >
        <div className="flex items-center justify-between mb-2">
          <Badge
            variant="outline"
            className={`gap-1 text-[11px] ${
              isWebhook
                ? "bg-sky-500/10 text-sky-600 border-sky-500/30"
                : "bg-primary/10 text-primary border-primary/30"
            }`}
          >
            {isWebhook ? (
              <Globe className="w-3 h-3" />
            ) : (
              <Sparkles className="w-3 h-3" />
            )}
            {actionMeta?.label || (isWebhook ? "Webhook" : "Ação")}
          </Badge>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(step.step_key)}
              className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground cursor-pointer"
            >
              Editar
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(step.step_key)}
              className="h-6 w-6 p-0 text-muted-foreground hover:text-rose-600 cursor-pointer"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Resumo da Ação */}
        <div className="text-xs text-foreground space-y-2">
          {isWebhook ? (
            <div className="font-mono text-[11px] text-muted-foreground truncate">
              POST {step.config?.url || "URL não configurada"}
            </div>
          ) : step.config?.tipo_acao === "criar_nota" ? (
            <div className="text-muted-foreground line-clamp-2">
              {step.config?.nota_text || actionMeta?.desc}
            </div>
          ) : (
            <>
              <div className="text-muted-foreground line-clamp-1">
                {step.config?.campos?.text ||
                  step.config?.campos?.name ||
                  step.config?.campos?.first_name ||
                  actionMeta?.desc}
              </div>
              {step.config?.campos &&
                Object.keys(step.config.campos).length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-0.5">
                    {Object.entries(step.config.campos).map(([k, v]) => (
                      <span
                        key={k}
                        className="px-2 py-0.5 rounded-md bg-muted/70 border border-border/60 text-[10px] text-muted-foreground font-mono"
                      >
                        <strong className="text-foreground font-sans">
                          {k}:
                        </strong>{" "}
                        {String(v)}
                      </span>
                    ))}
                  </div>
                )}
            </>
          )}
        </div>
      </div>

      {/* Renderização encadeada de passos subsequentes */}
      {childSteps.map((cStep) => (
        <React.Fragment key={cStep.step_key}>
          <StepConnector
            onAdd={(tipo) =>
              onAddChild(
                tipo,
                step.step_key,
                step.ramo_id || null,
                cStep.step_key,
              )
            }
          />
          <RenderStepNode
            step={cStep}
            allSteps={allSteps}
            onEdit={onEdit}
            onDelete={onDelete}
            onAddChild={onAddChild}
          />
        </React.Fragment>
      ))}

      {/* Se não houver filhos, mostra o conector para continuar a cadeia */}
      {childSteps.length === 0 && (
        <StepConnector
          onAdd={(tipo) =>
            onAddChild(tipo, step.step_key, step.ramo_id || null, null)
          }
        />
      )}
    </div>
  );
};
